<template>
  <div class="profile-form">
    <validation-observer ref="profileFormObserver" v-slot="{ passes, pristine }">
      <b-form class="profile-form__form form" @submit.stop.prevent="passes(submit)">
        <div class="profile-form-columns">
          <div class="profile-form-column">
            <forms-validated-input
              v-model="user.name"
              name="name"
              :label="$t('users.fields.name') | capitalize"
              :rules="{ required: true }"
              type="text"
              @keypress.native="onlyChars"
            />
            <forms-validated-input
              v-model="user.last_name"
              name="last_name"
              :label="$t('users.fields.last_name') | capitalize"
              :rules="{ required: true }"
              type="text"
            />
            <forms-validated-input
              v-model="user.phone"
              name="phone"
              :label="$t('users.fields.phone') | capitalize"
              :description="$t('components.profile.profile_form.phone.description')"
              :rules="{ required: true }"
              type="text"
              mask="(###) ###-####"
            />

            <forms-validated-input
              v-model="user.date_of_birth"
              name="date_of_birth"
              :label="$t('users.fields.date_of_birth') | capitalize"
              :rules="{
                required: true,
                date: true,
                before: $dayjs().subtract(18, 'years').format('YYYY-MM-DD'),
              }"
              type="date"
              :open-date="openDate"
              :disabled-dates-fct="datesInTheFuture"
            >
              <template #description>
                <i18n path="components.profile.profile_form.date_of_birth.description">
                  <template #privacy_policy_link>
                    <a :href="$t('conditions.privacy_policy_url')" target="_blank">
                      {{ $t("components.profile.profile_form.date_of_birth.privacy_policy") }}
                    </a>
                  </template>
                </i18n>
              </template>
            </forms-validated-input>
            <b-alert :variant="age < 18 ? 'danger' : 'warning'" :show="age < 21">
              {{ $t("components.profile.profile_form.age_restrictions.warning") }}
            </b-alert>
          </div>

          <div class="profile-picture-uploader profile-form-column">
            <div class="circle">
              <forms-validated-input
                v-model="user.avatar"
                type="image"
                :rules="{ required: true }"
                :label="$t('users.fields.avatar') | capitalize"
                :description="$t('users.descriptions.avatar')"
                name="avatar"
                preview-aspect-ratio="1 / 1"
              />
            </div>
            <svg-smiling-heart class="smiling-heart" />
          </div>
        </div>
        <forms-validated-input
          v-model="user.description"
          name="description"
          :description="$t('components.profile.profile_form.description.description')"
          :rules="{ required: true }"
          :label="$t('components.profile.profile_form.description.label')"
          type="textarea"
        />

        <div v-if="!skipMap" class="form-group">
          <label>{{ $t("components.profile.profile_form.address.label") }}</label>

          <validation-provider
            ref="addressValidator"
            v-slot="{ validated, valid, errors }"
            :rules="{ required: true, isFromGoogle: true }"
            name="adresse"
            mode="eager"
          >
            <place-autocomplete-input
              v-model="user.address"
              class="form-control"
              :class="{ 'is-invalid': validated && !valid, 'is-valid': validated && valid }"
              :options="{
                language: 'fr',
                fields: ['formatted_address'],
                componentRestrictions: { country: 'ca' },
                types: ['address'],
              }"
              @place-selected="placeSelected"
            />
            <b-form-invalid-feedback :state="validated ? valid : null">
              {{ errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>

          <small class="text-muted">
            {{ $t("components.profile.profile_form.address.description") }} <br />
            {{ $t("components.profile.profile_form.address.privacy_notice") }}
          </small>
        </div>

        <b-row v-else>
          <b-col>
            <forms-validated-input
              v-model="user.address"
              type="text"
              name="address"
              :label="$t('components.profile.profile_form.dev_mode.address_label')"
            />
          </b-col>
        </b-row>

        <slot />

        <div v-if="!hideButtons" class="form__buttons">
          <icon-button :loading="loading" variant="success" type="submit" :disabled="pristine">
            {{ $t("forms.save") | capitalize }}
          </icon-button>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import SmilingHeart from "@/assets/svg/smiling-heart.svg";
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PlaceAutocompleteInput from "@/components/shared/PlaceAutocompleteInput.vue";
import { isDevMode } from "@/helpers/devMode";

import FormLabelsMixin from "@/mixins/FormLabelsMixin";
import dayjs from "dayjs";

import { extend } from "vee-validate";

export default {
  name: "ProfileForm",
  components: {
    IconButton,
    PlaceAutocompleteInput,
    FormsValidatedInput,
    "svg-smiling-heart": SmilingHeart,
  },
  mixins: [FormLabelsMixin],
  props: {
    hideButtons: {
      type: Boolean,
      required: false,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      openDate: new Date("1985-01-07"),
      addressFromGoogle: null,
    };
  },
  computed: {
    age() {
      const age = this.$dayjs(this.$dayjs()).diff(this.user.date_of_birth, "year");

      if (Number.isNaN(age)) {
        return Number.MAX_SAFE_INTEGER;
      }

      return age;
    },
    skipMap() {
      return isDevMode();
    },
  },
  mounted() {
    if (!this.skipMap) {
      extend("isFromGoogle", {
        validate: (address) => address === this.addressFromGoogle,
        message: this.$t("components.profile.profile_form.address.validation.must_be_from_google"),
      });
      // We assume that initially created addresses are correct.
      this.addressFromGoogle = this.user.address ? this.user.address : null;
    }
  },
  methods: {
    placeSelected(event) {
      if (event && event.formatted_address) {
        this.addressFromGoogle = event.formatted_address;
        this.$nextTick(this.validate);
      }
    },
    onlyChars(event) {
      if (!event?.key) {
        return true;
      }

      if (event.key.match(/[0-9]/)) {
        event.preventDefault();
        return false;
      }

      return true;
    },
    datesInTheFuture(date) {
      return dayjs().isSameOrBefore(date);
    },
    submit(...params) {
      this.$emit("submit", ...params);
    },
  },
};
</script>

<style lang="scss">
.profile-form {
  container-type: inline-size;

  .profile-form-columns {
    display: flex;
    flex-direction: column;
    @container (width > 40rem) {
      flex-direction: row;
      gap: 2rem;
    }
  }

  .hide-label {
    label {
      display: none !important;
    }
  }

  h2.allo-title {
    text-align: left;
    margin: 15px 0 25px 0;
    svg {
      width: 60px;
      position: relative;
      top: -3px;
    }
  }

  .profile-picture-uploader {
    // Make the smiling heart position position relative to this container
    position: relative;

    // Selected image
    figure {
      max-width: 200px;
      margin: 0 auto;
      border-radius: 50%;
      overflow: hidden;
      box-shadow: $medium-shadow;
    }

    // hidden input, needs aspect ratio for the placeholder to fit the height
    .custom-file {
      max-width: 200px;
      aspect-ratio: 1 / 1;
      min-height: unset;
      height: unset;
      margin: auto;
      display: block;
    }

    // Image placeholder
    .custom-file-label {
      border: none;
      width: 100%;
      height: 100%;
      background-color: #00ada8;
      border-radius: 50%;
      overflow: inherit;
      background-image: url("/white-female-face.svg");
      background-position: top 40% center;
      &::after {
        background-color: #245ae9;
        border-radius: 32px;
        font-size: 16px;
        color: white;
        bottom: 1rem;
        font-weight: 600;
        z-index: 4;
      }
    }

    .smiling-heart {
      position: absolute;
      z-index: 10;
      left: 50%;
      margin-left: 1rem;
      top: 0;
      width: 6rem;
      max-width: 50%;
      transform: rotate(15deg);
    }

    button {
      display: block;
      margin: 20px auto;
    }
  }

  .form__buttons {
    margin-top: 20px;
    .btn-primary {
      margin-left: 0;
    }
  }

  .safety-questions {
    h2,
    h3 {
      color: #047249;
    }

    h3 {
      font-size: 18px;
    }
  }
}
</style>
